import type { CreateOrganizationGpuJobInput, OrganizationGpuJobDto } from "dutyon-server/types";
import { useAuthStore } from "../auth/authStore";
import { httpClient } from "./httpClient";
import {
  type OrganizationGpuSessionStore,
  withOrganizationGpuSession,
} from "./organization-gpu-session";

const session: OrganizationGpuSessionStore = {
  getState: () => useAuthStore.getState(),
  subscribe: (listener) => useAuthStore.subscribe(listener),
};
function path(id: string) {
  if (!/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(id))
    throw new Error("생성 작업 ID를 확인해 주세요.");
  return `/organization-generation-jobs/${encodeURIComponent(id)}`;
}
/** Preserve input/requestKey for explicit retry after an ambiguous response; never select a CPU fallback. */
export function createOrganizationGpuJob(
  owner: string,
  input: CreateOrganizationGpuJobInput,
  signal?: AbortSignal,
): Promise<OrganizationGpuJobDto> {
  const snapshot = structuredClone(input);
  return withOrganizationGpuSession(
    session,
    owner,
    signal,
    async (config) =>
      (
        await httpClient.post<OrganizationGpuJobDto>("/organization-generation-jobs", snapshot, {
          ...config,
          timeout: 30000,
        })
      ).data,
  );
}
export function getOrganizationGpuJob(
  owner: string,
  id: string,
  signal?: AbortSignal,
): Promise<OrganizationGpuJobDto> {
  const target = path(id);
  return withOrganizationGpuSession(
    session,
    owner,
    signal,
    async (config) =>
      (await httpClient.get<OrganizationGpuJobDto>(target, { ...config, timeout: 30000 })).data,
  );
}
/** Cancels cloud result acceptance. Native work already running can finish without being applied. */
export function cancelOrganizationGpuJob(
  owner: string,
  id: string,
  signal?: AbortSignal,
): Promise<OrganizationGpuJobDto> {
  const target = `${path(id)}/cancel`;
  return withOrganizationGpuSession(
    session,
    owner,
    signal,
    async (config) =>
      (
        await httpClient.post<OrganizationGpuJobDto>(target, undefined, {
          ...config,
          timeout: 10000,
        })
      ).data,
  );
}
